import * as React from "react"
import {
    DataGrid,
} from "@mui/x-data-grid"
import { AXIOS_API_INSTANCE } from "../../Constants"
import { send_authorized_request } from "../../Constants/cognito"

import { SimpleCustomToolbar } from "../CustomToolbar"
import onFilterChange from "../../Utils/OnFilterChange"
import { Box, Button, InputAdornment, TextField, debounce } from "@mui/material"
import Grid from "@mui/material/Grid2"
import SearchIcon from '@mui/icons-material/Search';
import AddCircleSharpIcon from '@mui/icons-material/AddCircleSharp';
import ServiceKitModal from "./Modals/ServiceKitModal"
import { moneyFormat, widthChangeEvent } from "../../Utils"
import DocumentTitle from "../../Utils/DocumentTitle"

const columns = [
    {
        field: "name",
        headerName: "Name",
        minWidth: 100,
        flex: 2,
        editable: false,
        filterable: false,
    },
    {
        field: "description",
        headerName: "Description",
        flex: 3,
        editable: false,
        filterable: false,
    },
    {
        field: "default_flat_rate",
        headerName: "Default Flat Rate",
        flex: 1,
        editable: false,
        filterable: false,
        valueFormatter: (value) => `$${moneyFormat(value)}`
    },
    {
        field: "hourly_rate",
        headerName: "Hourly Rate",
        flex: 1,
        editable: false,
        filterable: false,
        valueFormatter: (value) => `$${moneyFormat(value)}`
    },
    {
        field: "default_minutes_worked",
        headerName: "Default Minutes Worked",
        flex: 1,
        editable: false,
        filterable: false,
    },
    {
        field: "part_price_adjustment",
        headerName: "Part Price Adjustment",
        flex: 1,
        editable: false,
        filterable: false,
        valueFormatter: (value) => `${value >= 0 ? '+' : ''}${value}%`
    },
]

export default function ServiceKits(props) {
    DocumentTitle(`Service Kits`)
    const [paginationModel, setPaginationModel] = React.useState({
        pageSize: parseInt(localStorage.getItem(`servicekits:pageSize`) || 25),
        page: 0,
    });
    const [rowData, setRowData] = React.useState({ rows: [], total_count: 0 })
    const [sorting, setSorting] = React.useState({ field: 'name', sort: 'asc' })
    const [filtering, setFiltering] = React.useState({})
    const [isLoading, setIsLoading] = React.useState(true)
    const [serviceKitModal, setServiceKitModal] = React.useState({
        open: false,
        servicekit: {},
        creatingNewServiceKit: false,
    })
    const [serviceKitSearch, setServiceKitSearch] = React.useState('')
    const [windowInnerWidth, setWindowInnerWidth] = React.useState(window.innerWidth)

    const serviceKitRowClick = (params) => {
        setServiceKitModal({
            servicekit: params.row,
            open: true,
            creatingNewServiceKit: false,
        })
    }
    const createServiceKitClick = (params) => {
        setServiceKitModal({
            servicekit: {
                name: '',
                description: '',
                default_flat_rate: 0,
                flat_rate_sku: '',
                hourly_rate: 0,
                hourly_rate_sku: '',
                default_minutes_worked: 0,
                part_price_adjustment: 0,
                part_coupon_sku: '',
                taxable_flat_rate: true,
                taxable_hourly_rate: true,
                taxable_parts: true,
            },
            open: true,
            creatingNewServiceKit: true,
        })
    }
    const closeServiceKitModal = (changes) => {
        setServiceKitModal({ ...serviceKitModal, open: false })
        if (changes) {
            getServiceKits(sorting.field, sorting.sort, paginationModel, serviceKitSearch)
        }
    }

    const setMyPaginationModel = (model) => {
        localStorage.setItem(`servicekits:pageSize`, model.pageSize)
        setPaginationModel(model)
    }
    const updateServiceKitSearch = (event) => {
        setServiceKitSearch(event.target.value.trim())
        if (event.target.value.trim() !== serviceKitSearch) {
            debouncedGetServiceKits(sorting.field, sorting.sort, paginationModel, event.target.value.trim())
        }
    }

    const getServiceKits = (sortField, sortDirection, paginationModel, serviceKitSearch) => {
        setIsLoading(true)
        let my_filters = {}
        if (serviceKitSearch.length > 0) {
            my_filters['or'] = [
                { 'name.ilike': serviceKitSearch },
                { 'description.ilike': serviceKitSearch },
            ]
        }
        send_authorized_request(() => AXIOS_API_INSTANCE.get("servicekits", {
            params: {
                page_num: paginationModel.page,
                page_size: paginationModel.pageSize,
                order_by: sortField,
                order_by_direction: sortDirection,
                filters: JSON.stringify(my_filters),
                check_deletable: true
            },
        }).then((servicekits) => {
            setRowData({
                rows: servicekits.data.data,
                total_count: servicekits.data.total_count,
            })
            setIsLoading(false)
        })
        )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedGetServiceKits = React.useMemo(() => debounce((sortField, sortDirection, paginationModel, serviceKitSearch) => getServiceKits(sortField, sortDirection, paginationModel, serviceKitSearch), 400), []);


    React.useEffect(() => {
        getServiceKits(sorting.field, sorting.sort, paginationModel, serviceKitSearch)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationModel, filtering, sorting])

    React.useEffect(() => {
        return widthChangeEvent(setWindowInnerWidth)
    }, [])

    return (
        (<React.Fragment>
            <Grid container space={1} sx={{ width: '100%' }}>
                <Grid size="grow">
                    <Grid container sx={{ padding: "0" }} justifyContent={"space-between"} alignItems={"center"}>
                        <Grid>
                            <TextField
                                autoFocus
                                margin="dense"
                                id="serviceKitSearch"
                                label="Search by Name/Description"
                                type="text"
                                variant="filled"
                                disabled={isLoading}
                                value={serviceKitSearch}
                                onChange={updateServiceKitSearch}
                                slotProps={{
                                    input: {
                                        inputProps: { min: 1 },
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                {<SearchIcon />}
                                            </InputAdornment>
                                        ),
                                    }
                                }}
                            />
                        </Grid>
                        <Grid>
                            <Button onClick={createServiceKitClick} variant="contained" color="primary"><AddCircleSharpIcon /><Box sx={{ pl: 1 }}>Add Service Kit</Box></Button>
                        </Grid>
                    </Grid>
                    <DataGrid
                        key={windowInnerWidth}
                        slots={{
                            toolbar: () => SimpleCustomToolbar(),
                        }}
                        rows={rowData.rows}
                        rowCount={rowData.total_count}
                        columns={columns}
                        disableRowSelectionOnClick
                        experimentalFeatures={{ newEditingApi: true }}
                        autoHeight={true}
                        filterMode={"server"}
                        loading={isLoading}
                        pageSizeOptions={[5, 10, 25, 50, 100]}
                        pagination
                        paginationMode="server"
                        paginationModel={paginationModel}
                        onPaginationModelChange={setMyPaginationModel}
                        onRowClick={serviceKitRowClick}
                        onSortModelChange={(params) => {
                            if (params[0] !== undefined) {
                                setSorting(params[0])
                            } else {
                                setSorting({ field: 'name', sort: 'asc' })
                            }
                        }}
                        onFilterModelChange={(params) => { setFiltering(onFilterChange(params)) }}
                        disableColumnMenu
                    />
                    <ServiceKitModal
                        open={serviceKitModal.open}
                        servicekit={serviceKitModal.servicekit}
                        closeModal={closeServiceKitModal}
                        creatingNewServiceKit={serviceKitModal.creatingNewServiceKit}
                    />
                </Grid>
            </Grid>
        </React.Fragment>)
    );
}
